"use client";

import { useEffect, useRef, useState } from "react";

import { Icon } from "@/components/ui/icon";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { PriceRange } from "@/components/ui/price-range";
import { SkeletonBar, SkeletonPulse } from "@/components/ui/skeleton";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import {
  FACETS,
  facetOptions,
  serverOptions,
  hasPriceRange,
  priceSummary,
  selectedCount,
  type FacetFilters,
  type FacetKey,
  type FacetOption,
  type PriceRange as PriceRangeValue,
} from "@/lib/host-panel/catalog-facets";
import { audienceOffersPrice, type CatalogOrderAudience } from "@/lib/host-panel/catalog-order";
import { priceHistogram, serverPriceHistogram } from "@/lib/host-panel/catalog-price-histogram";
import type { CatalogFacets, ListedPiece } from "@/lib/host-panel/catalog-types";
import { PANEL_LAYER_CLASS } from "@/lib/host-panel/layer-stack";
import { useIsWideViewport } from "@/lib/host-panel/use-is-wide-viewport";
import { cn } from "@/lib/utils";
import { CatalogChip } from "./catalog-filter-chip";
import { CatalogFilterRow } from "./catalog-filter-row";
import { FacetPanel } from "./facet-panel";

/**
 * The "Atributos" line of the filter block (handoff v2 §6.2, filters spec §5): Tipo, Coleção,
 * Material and Faixa de preço, then "Destaques" and "Limpar filtros".
 *
 * Each attribute opens its own layer — a panel anchored under the chip from 1024px, a bottom
 * sheet below (§7). An unmarked chip reads how many values it offers, "(N)"; a marked one, how
 * many are marked. The counter and the order control left this line in v2: they live in the
 * sticky bar under the block (`catalog-toolbar.tsx`).
 */

/** §7: the list's height in the anchored panel and in the sheet. */
const POPOVER_LIST_CLASS = "max-h-[304px]";
const SHEET_LIST_CLASS = "max-h-[58svh]";

export type FacetRailProps = {
  /** Pieces after the taxonomy; the options are counted over these. */
  scoped: readonly ListedPiece[];
  filters: FacetFilters;
  onFiltersChange: (next: FacetFilters) => void;
  /** How many pieces the slice shows, which the sheet's action reads. */
  visibleCount: number;
  /** The slice's page has not arrived: the sheet's action cannot state a count yet (a skeleton). */
  isCountPending?: boolean;
  featuredCount: number;
  /**
   * Options counted by the server (filters spec §5), for the scope it filters: the catalog to
   * explore. Absent in the list scope, where the options are counted over `scoped` right here.
   */
  serverFacets?: CatalogFacets;
  /** What "Limpar filtros" does where more than the attributes clears. */
  onClearFilters?: () => void;
  /** Whether a search term is applied: it narrows the grid, so it counts as a filter. */
  hasAppliedSearch?: boolean;
  /** The phone's sheet covers the floating mode control, which steps aside while it is open. */
  onSheetOpenChange?: (isOpen: boolean) => void;
  /** Whose context this is; the price facet is hidden where the context shows no price (§5). */
  audience?: CatalogOrderAudience;
  /** Whether the chosen department is "Coleções", whose second level is the collection (§5). */
  isCollectionsDepartment?: boolean;
};

export function FacetRail({
  scoped,
  filters,
  onFiltersChange,
  visibleCount,
  isCountPending = false,
  featuredCount,
  serverFacets,
  onClearFilters,
  hasAppliedSearch = false,
  onSheetOpenChange,
  audience = "gestao",
  isCollectionsDepartment = false,
}: FacetRailProps) {
  const [openFacet, setOpenFacet] = useState<FacetKey | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const chipsRef = useRef<HTMLDivElement>(null);
  const isWide = useIsWideViewport();

  const isSheetOpen = openFacet !== null && !isWide;
  useEffect(() => onSheetOpenChange?.(isSheetOpen), [isSheetOpen, onSheetOpenChange]);

  /**
   * The range the slider spans. Over the served catalogue the bounds are the catalogue's own, in
   * the unit the backend filters by — the product's price — so the bucket the person aims at is
   * the one the filter reads.
   */
  const histogram = serverFacets === undefined ? priceHistogram(scoped) : serverPriceHistogram(scoped, serverFacets);

  function closeFacet() {
    setOpenFacet(null);
    setSearchTerm("");
  }

  function openOrClose(facet: FacetKey) {
    setSearchTerm("");
    setOpenFacet((current) => (current === facet ? null : facet));
  }

  function toggleOption(facet: Exclude<FacetKey, "preco">, value: string) {
    const next = new Set(filters.selection);
    const key = `${facet}:${value}`;
    if (next.has(key)) next.delete(key);
    else next.add(key);

    onFiltersChange({ ...filters, selection: next });
  }

  function applyPrice(range: PriceRangeValue) {
    onFiltersChange({ ...filters, price: range });
    closeFacet();
  }

  const optionsOf = (facet: Exclude<FacetKey, "preco">): FacetOption[] =>
    serverFacets === undefined
      ? facetOptions(scoped, facet, filters.selection)
      : serverOptions(serverFacets, facet, filters.selection);

  /**
   * §5: a facet with fewer than two values in the scope does not appear, and price disappears
   * where the scope carries none. A marked facet always draws: the backend stops counting the
   * other axes once a collection is picked, and a chip that vanished would take the only way to
   * undo the mark with it.
   */
  const visibleFacets = FACETS.filter((facet) => {
    // §5: hidden where the context shows no price (`semValor` in the prototype), whatever the
    // scope carries — a range left over from another context does not bring the chip back.
    if (facet.key === "preco" && !audienceOffersPrice(audience)) return false;
    // §5: under the "Coleções" department the second level already **is** the collection, so the
    // facet would draw the same choice twice (the designer's afirmação D). A mark made earlier
    // does not keep it: changing department clears the marks.
    if (facet.key === "colecao" && isCollectionsDepartment) return false;
    if (facet.key === "preco") return histogram !== null || hasPriceRange(filters.price);

    return optionsOf(facet.key).length > 1 || selectedCount(filters.selection, facet.key) > 0;
  });

  // Nothing featured in the scope, nothing to toggle: a chip that empties the grid is a trap.
  const hasFeatured = featuredCount > 0 || filters.onlyFeatured;

  const hasAnyFilter =
    filters.selection.size > 0 ||
    filters.onlyFeatured ||
    hasPriceRange(filters.price) ||
    // The applied search narrows the grid like any mark, and is cleared by the same control.
    hasAppliedSearch;

  // The line itself exists only with something on it (`passoRapidoDisplay`).
  if (visibleFacets.length === 0 && !hasFeatured) return null;

  return (
    <CatalogFilterRow
      label="Atributos"
      count={`(${visibleFacets.length + (hasFeatured ? 1 : 0)})`}
      className="gap-y-3 [&>span:first-child]:pt-0.5 [@media(min-width:1024px)]:[&>span:first-child]:pt-0"
    >
      {/*
        Two siblings: the chips, which may scroll or wrap, and "Limpar filtros", which sits **outside**
        that scroller so it stays anchored to the right instead of travelling with the chips. The
        chips take the room it leaves (`flex-1 min-w-0`), so the last chip never runs under it.
      */}
      <div className="flex min-w-0 items-start gap-3">
      {/* Wraps from 1024px; on a phone the attributes scroll sideways like every other track. */}
      <div
        ref={chipsRef}
        className="flex min-w-0 flex-1 flex-nowrap items-center gap-2 overflow-x-auto py-px [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [@media(min-width:1024px)]:flex-wrap [@media(min-width:1024px)]:overflow-x-visible"
      >
        {visibleFacets.map((facet) => {
          const count = facet.key === "preco" ? 0 : selectedCount(filters.selection, facet.key);
          const isActive = facet.key === "preco" ? hasPriceRange(filters.price) : count > 0;
          const isOpen = openFacet === facet.key;
          const options = facet.key === "preco" ? [] : optionsOf(facet.key);
          // The price facet counts nothing: a range is one filter, and it offers no values.
          const chipCount = facet.key === "preco" ? undefined : isActive ? String(count) : `(${options.length})`;

          /**
           * On the anchored path `PopoverTrigger` is what toggles, so the chip goes **without**
           * `onClick`: with both, one tap opened and closed in the same gesture.
           */
          const renderChip = (onClick?: () => void) => (
            <CatalogChip
              label={facet.label}
              count={chipCount}
              isActive={isActive}
              chevron={{ isOpen }}
              aria-expanded={isOpen}
              onClick={onClick}
            />
          );

          if (isWide) {
            return (
              <Popover
                key={facet.key}
                open={isOpen}
                onOpenChange={(next) => {
                  if (next) setOpenFacet(facet.key);
                  else closeFacet();
                  setSearchTerm("");
                }}
              >
                <PopoverTrigger asChild>{renderChip()}</PopoverTrigger>
                <PopoverContent
                  align="start"
                  sideOffset={8}
                  hideArrow
                  aria-label={facet.label}
                  className={cn(
                    PANEL_LAYER_CLASS.facetPopover,
                    // The prototype's panel: square, its shadow cast upward, 14px 16px 16px. Portalled
                    // out of the panel root, so the reference's base type is restated.
                    "flex w-auto min-w-[272px] max-w-[360px] flex-col gap-3 rounded-none px-4 pb-4 pt-3.5 text-[15px] leading-[1.65] shadow-[0_-8px_40px_rgb(61_63_57_/_0.16)]",
                    "data-[state=open]:[animation:tb-fade-in_260ms_var(--tb-ease-linen),tb-facet-rise_340ms_var(--tb-ease-linen)]",
                    // The price panel is 260px wide at its least, where the others are 272px.
                    facet.key === "preco" && "w-[260px] min-w-[260px]",
                  )}
                >
                  {facet.key === "preco" && histogram !== null ? (
                    <PriceFacetBody
                      histogram={histogram}
                      price={filters.price}
                      onApply={applyPrice}
                      onClear={() => applyPrice({ minCents: null, maxCents: null })}
                    />
                  ) : (
                    <FacetPanel
                      facet={facet.key}
                      options={options}
                      totalOptions={options.length}
                      searchTerm={searchTerm}
                      onSearchChange={setSearchTerm}
                      onToggle={(value) => toggleOption(facet.key as Exclude<FacetKey, "preco">, value)}
                      listClassName={POPOVER_LIST_CLASS}
                    />
                  )}
                </PopoverContent>
              </Popover>
            );
          }

          return (
            <span key={facet.key} className="flex-none">
              {renderChip(() => openOrClose(facet.key))}

              {isOpen ? (
                <Drawer open onOpenChange={(next) => (next ? undefined : closeFacet())}>
                  <DrawerContent
                    side="bottom"
                    aria-label={facet.label}
                    overlayClassName={cn(
                      PANEL_LAYER_CLASS.facetBackdrop,
                      "bg-[rgb(61_63_57_/_0.40)] data-[state=open]:[animation:tb-fade-in_500ms_var(--tb-ease-linen)]",
                    )}
                    className={cn(
                      PANEL_LAYER_CLASS.facetSheet,
                      // Portalled out of the panel root: the reference's base type is restated.
                      "max-h-[82svh] gap-3.5 rounded-none border-t border-[var(--tb-border)] px-5 pb-6 pt-[18px] text-[15px] leading-[1.65] shadow-[0_-12px_40px_rgb(61_63_57_/_0.18)]",
                      "data-[state=open]:[animation:tb-fade-in_420ms_var(--tb-ease-linen),tb-facet-sheet-rise_620ms_var(--tb-ease-linen)]",
                    )}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <DrawerTitle className="font-display text-[18px] font-light uppercase leading-[1.15]">
                        {facet.label}
                      </DrawerTitle>
                      <button
                        type="button"
                        onClick={closeFacet}
                        aria-label="Fechar"
                        /*
                         * The design's 36px disc: the box **is** 36px, so the header row is 36px
                         * tall. The touch target still reaches 44px — the `before:` overhangs the
                         * disc by 4px without taking room.
                         */
                        className="relative grid size-9 flex-none cursor-pointer place-content-center rounded-full border border-[var(--tb-border)] px-[6px] py-px before:absolute before:-inset-1 before:content-['']"
                      >
                        <Icon name="close" size={15} />
                      </button>
                    </div>

                    {facet.key === "preco" && histogram !== null ? (
                      <>
                        <PriceFacetBody
                          histogram={histogram}
                          price={filters.price}
                          onApply={applyPrice}
                          onClear={() => applyPrice({ minCents: null, maxCents: null })}
                          density="sheet"
                        />
                        {/* The prototype's sheet keeps its (empty) option list under the range, so
                            the sheet's 14px gap counts once more before the closing action. */}
                        <span aria-hidden="true" className="block" />
                      </>
                    ) : (
                      <FacetPanel
                        facet={facet.key}
                        options={options}
                        totalOptions={options.length}
                        searchTerm={searchTerm}
                        onSearchChange={setSearchTerm}
                        onToggle={(value) => toggleOption(facet.key as Exclude<FacetKey, "preco">, value)}
                        listClassName={SHEET_LIST_CLASS}
                        density="sheet"
                      />
                    )}

                    {/* Marks apply at once (§5); this closes the sheet, naming what the grid holds. */}
                    <button
                      type="button"
                      onClick={closeFacet}
                      className="flex min-h-12 cursor-pointer items-center justify-center gap-2.5 bg-tb-green px-[22px] py-3.5 text-[12px] font-medium uppercase tracking-[0.16em] text-[var(--tb-porcelain)] focus-visible:outline-none focus-visible:shadow-[var(--focus-ring)]"
                    >
                      <span>Aplicar filtros</span>
                      <span className="font-mono text-[11px] tracking-[0.04em] text-[rgb(245_237_230_/_0.72)]">
                        {isCountPending ? (
                          <span aria-busy="true" data-testid="facet-sheet-count-skeleton" className="flex h-4 items-center">
                            <SkeletonPulse>
                              <SkeletonBar height={11} width={64} />
                            </SkeletonPulse>
                          </span>
                        ) : (
                          `${visibleCount.toLocaleString("pt-BR")} ${visibleCount === 1 ? "peça" : "peças"}`
                        )}
                      </span>
                    </button>
                  </DrawerContent>
                </Drawer>
              ) : null}
            </span>
          );
        })}

        {/* Featured sits beside the attributes but is not one: a toggle with no option list. */}
        {hasFeatured ? (
          <CatalogChip
            label="Destaques"
            count={String(featuredCount)}
            countSize="caption"
            isActive={filters.onlyFeatured}
            aria-pressed={filters.onlyFeatured}
            onClick={() => {
              closeFacet();
              onFiltersChange({ ...filters, onlyFeatured: !filters.onlyFeatured });
            }}
          />
        ) : null}

      </div>

      {/*
        Drawn only with something to clear: a mark, the featured toggle, a price range or an applied
        search. This DEPARTS from handoff §8, which says the control "ocupa seu espaço sempre,
        inerte e sem opacidade quando não há filtro"; the designer asked for it and the product
        owner decided it (2026-10-21). A search that was typed but not sent narrows nothing, so it
        does not bring the button. It clears marks, featured, the range and the search — **not**
        the department or category, which are navigation rather than refinement.
      */}
      {hasAnyFilter ? (
        <button
          type="button"
          onClick={() => {
            closeFacet();
            if (onClearFilters !== undefined) onClearFilters();
            else {
              onFiltersChange({
                selection: new Set(),
                price: { minCents: null, maxCents: null },
                onlyFeatured: false,
              });
            }

            // The button leaves the document under the focus: hand it to the first chip, so a
            // keyboard user is not dropped on the page body.
            requestAnimationFrame(() => chipsRef.current?.querySelector("button")?.focus());
          }}
          className="flex-none cursor-pointer whitespace-nowrap px-3.5 py-[9px] font-sans text-[12px] text-tb-green focus-visible:outline-none focus-visible:shadow-[var(--focus-ring)]"
        >
          <span className="border-b border-current">Limpar filtros</span>
        </button>
      ) : null}
      </div>
    </CatalogFilterRow>
  );
}

/** Body of the price facet, over the registry's `@tb/price-range`. */
function PriceFacetBody({
  histogram,
  price,
  onApply,
  onClear,
  density = "popover",
}: {
  histogram: NonNullable<ReturnType<typeof priceHistogram>>;
  price: PriceRangeValue;
  onApply: (range: PriceRangeValue) => void;
  onClear: () => void;
  /** The summary reads at 11.5px in the desktop panel and at 12px in the phone's sheet. */
  density?: "popover" | "sheet";
}) {
  const floor = histogram.buckets[0];

  return (
    <>
      <span className="flex items-baseline justify-between gap-2.5">
        <span className="text-[10px] uppercase tracking-[0.16em] text-[var(--fg-subtle)]">Total do conjunto</span>
        <span className={cn("font-mono text-[var(--fg-muted)]", density === "sheet" ? "text-[12px]" : "text-[11.5px]")}>
          {priceSummary(price)}
        </span>
      </span>

      {/*
       * The widget works in reais, which is what people type, while the filter state is in integer
       * cents: this boundary is where the conversion happens. Uncontrolled on purpose —
       * `@tb/price-range` exposes no `onChange` (T16); the `key` reanchors its seed.
       */}
      <PriceRange
        key={`${price.minCents ?? ""}-${price.maxCents ?? ""}-${histogram.buckets.join()}`}
        buckets={histogram.buckets.map((cents) => cents / 100)}
        counts={histogram.counts}
        labels={histogram.labels}
        min={floor / 100}
        max={histogram.maxCents / 100}
        defaultValue={{
          min: (price.minCents ?? floor) / 100,
          max: (price.maxCents ?? histogram.maxCents) / 100,
        }}
        onApply={(range) => onApply({ minCents: Math.round(range.min * 100), maxCents: Math.round(range.max * 100) })}
        applyLabel="Aplicar faixa"
        className="w-full"
      />

      {hasPriceRange(price) ? (
        <button
          type="button"
          onClick={onClear}
          className={cn(
            "cursor-pointer self-start border-b border-[var(--tb-border)] text-[var(--fg-muted)]",
            // The sheet's is a 44px row at 14px; the panel's, a caption underlined.
            density === "sheet" ? "min-h-[var(--tb-touch-min)] border-b-0 text-[14px]" : "text-[12px]",
          )}
        >
          Limpar faixa
        </button>
      ) : null}
    </>
  );
}
